import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  LogOut, Loader2, CheckCircle, AlertCircle,
  Menu, X, Sun, Moon,
  ChevronsLeft, ChevronsRight,
  LayoutDashboard, Activity, FileBarChart, Sparkles, Database, Radio,
  Scale, Crosshair, ShieldCheck, HeartPulse, IdCard, Plug, Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { endSession } from '../lib/auth';
import { api } from '../lib/api';
import { fetchProfile, PROFILE_QUERY_KEY, PROFILE_STALE_TIME } from '../lib/profile';
import { canSeeTab, useMyPermissions } from '../lib/tabPermissions';
import { useClubBranding } from '../lib/clubBranding';
import { useClubLanguage } from '../lib/clubLanguage';
import { useSidebarCollapsed } from '../lib/useSidebarCollapsed';
import { cn } from '../lib/cn';
import { PlayerAvatar } from './PlayerAvatar';
import { proxiedImageUrl } from '../lib/imageProxy';
import { activateClubContext, getSuperAdminClubId, setSuperAdminClubId } from '../lib/superAdminStore';
import { MDChip, ArgusMark } from './cockpit';
import { LIVE_COLORS, cA } from './cockpit/colors';
import { useNextFixture, daysAwayToMd, daysAwayToLabel } from '../lib/api/fixtures';
import { ToastHost, ConfirmHost } from './feedback';

const COCKPIT_ROUTES = ['/injuries', '/passport'];

type CockpitNavItem =
  | { type: 'item'; key: string; path: string; Icon: LucideIcon; badge?: number; badgeColor?: string; role?: 'admin' | 'super_admin' }
  | { type: 'divider'; role?: 'admin' };

// Sidebar order is a PRODUCT decision, set by the club — the seven entries below the first
// comment are the ones staff reach for daily, in the order they asked for; everything after
// keeps its previous relative order. The `admin` entry renders as "Preferences" for non-admins
// (see the label ternary in the nav render), which is why it sits inside the top group rather
// than down with the admin tools.
// Note this is ORDER only — visibility is still driven by the club's permission matrix, so a
// role that cannot see a page simply doesn't get that row.
//
// Icons are Lucide, not the unicode glyphs this list used to carry. The glyphs were fine
// beside a label and useless without one: ▥ Reports, ▦ Data Room and ◧ Dashboard are three
// square-fill variants, and ⬡ Passport vs ⬢ Integration differed only by fill — so the
// collapsed rail would have been a row of identical squares. A single-family icon set also
// removes the reason the glyph slot needed a fixed width and centre-align, which was there
// to force mismatched baselines into a column.
const COCKPIT_NAV: CockpitNavItem[] = [
  // First, because it is the landing page (TAB_PATHS in lib/tabPermissions.ts, same order).
  { type: 'item', key: 'dashboard',   path: '/dashboard',    Icon: LayoutDashboard },
  { type: 'item', key: 'sessionOverview', path: '/session-overview', Icon: Activity },
  { type: 'item', key: 'reports',     path: '/reports',      Icon: FileBarChart },
  { type: 'item', key: 'promptLab',   path: '/prompt-lab',   Icon: Sparkles },
  { type: 'item', key: 'dataroom',    path: '/data-room',    Icon: Database },
  { type: 'item', key: 'live',        path: '/live',         Icon: Radio },
  { type: 'item', key: 'loadrisk',    path: '/load-risk',    Icon: Scale },
  { type: 'item', key: 'scouting',    path: '/opponent-lab', Icon: Crosshair },
  // ── everything else, previous relative order ──
  { type: 'item', key: 'injuries',    path: '/injuries',     Icon: HeartPulse, badge: 0, badgeColor: '#f97316' },
  { type: 'item', key: 'passport',    path: '/passport',     Icon: IdCard, badge: 0, badgeColor: '#3b82f6' },
  { type: 'item', key: 'integration', path: '/integration',  Icon: Plug },
  // ── account / administration, below the rule ──
  // The divider is no longer admin-gated. It was, so the sidebar wouldn't end on a
  // stray rule when the only row beneath it (Super Admin) was hidden. Admin now
  // sits below the rule and is visible to EVERY role — as "Preferences" for
  // non-admins (see the filter and label below) — so there is always something
  // after it and the stray-rule case can't happen.
  { type: 'divider' },
  { type: 'item', key: 'superAdmin',  path: '/super-admin',  Icon: Zap, role: 'super_admin' },
  { type: 'item', key: 'admin',       path: '/admin',        Icon: ShieldCheck, role: 'admin' },
];

// Inject the shell's hover/focus styles once.
//
// These live in a stylesheet rather than inline styles because inline styles
// beat every class: a `hover:text-*` utility on an element that also carries
// `style={{ color }}` silently does nothing, which is how the sign-out button
// ended up with a hover state that never fired.
let shellStyleInjected = false;
function ensureShellStyle() {
  if (shellStyleInjected || typeof document === 'undefined') return;
  if (document.getElementById('shell-chrome-style')) { shellStyleInjected = true; return; }
  const s = document.createElement('style');
  s.id = 'shell-chrome-style';
  s.textContent = `
.cockpit-nav-hover:hover { background: ${cA(LIVE_COLORS.white, '10')} !important; }
.shell-icon-btn { color: var(--text-muted); transition: color .15s ease, background-color .15s ease; }
.shell-icon-btn:hover { color: var(--text-primary); background: var(--bg-surface-hover); }
.shell-icon-btn-danger:hover { color: #f87171; background: rgba(239,68,68,0.12); }
.shell-focus:focus-visible { outline: 2px solid var(--club-primary); outline-offset: 2px; }
`;
  document.head.appendChild(s);
  shellStyleInjected = true;
}

/**
 * The Argus lockup — A-Eye tile + wordmark.
 *
 * Shared by the desktop sidebar and the mobile topbar so the two cannot drift.
 * When the club has a brand colour the WHOLE lockup adopts it, so the sidebar
 * carries one accent instead of stacking Argus violet directly above a
 * club-coloured club pill and nav. With no club colour configured it falls back
 * to the Argus violet gradient, which is the unbranded product identity.
 *
 * The branded wordmark is a FLAT fill on purpose: a gradient has two endpoints
 * and --club-ink only guarantees 4.5:1 at its own value.
 *
 * `collapsed` drops the wordmark and keeps the mark alone — the icon rail has no
 * room for "Argus / Sports", and ArgusMark already carries aria-label="Argus Sports"
 * so nothing is lost to a screen reader.
 */
function ArgusLockup({ tile, title, sub, collapsed = false }: { tile: number; title: number; sub: number; collapsed?: boolean }) {
  const { data: profile } = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    staleTime: PROFILE_STALE_TIME,
  });
  const branded = Boolean(profile?.club?.primaryColor);

  // The tile is always a dark squircle, so it takes the dark-surface ink
  // whichever theme the app itself is in.
  const mark = <ArgusMark size={tile} color={branded ? 'var(--club-ink-dark)' : undefined} />;

  if (collapsed) {
    return <div style={{ display: 'flex', justifyContent: 'center' }}>{mark}</div>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
      {mark}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: title, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15,
            ...(branded
              ? { color: 'var(--club-ink)' }
              : {
                  background: 'linear-gradient(120deg, #4F8DF5, #A06BFF)',
                  WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
                }),
          }}
        >
          Argus
        </div>
        <div style={{
          fontSize: sub, color: 'var(--text-muted)', letterSpacing: '0.1em',
          textTransform: 'uppercase', fontWeight: 700, marginTop: 1,
        }}>
          Sports
        </div>
      </div>
    </div>
  );
}


function getInitials(name: string) {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  coach: 'Coach',
  physio: 'Physiotherapist',
  analyst: 'Analyst',
  player_view: 'Player',
  stakeholder: 'Stakeholder',
  super_admin: 'Super Admin',
};

/**
 * Club switcher shown in the sidebar for super admin users.
 *
 * Fetches all clubs from the platform endpoint and lets the super admin choose
 * which club's data they are currently viewing. The selected club ID is stored
 * in sessionStorage and attached to every API request as the x-club-id header;
 * the club's tenant ID is stored alongside it and sent as X-Tenant-Id, which is
 * what actually scopes club data on the backend.
 *
 * When no club is selected, a warning prompt is displayed so it is obvious
 * that the super admin needs to pick a club before interacting with club data.
 */
function ClubSwitcher() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Keep a local copy of the active club ID so the component re-renders
  // immediately when the user picks a different club from the dropdown.
  const [activeClubId, setActiveClubId] = React.useState<string | null>(getSuperAdminClubId);

  const { data: clubListData } = useQuery({
    queryKey: ['super-admin-club-list'],
    queryFn: async () => {
      const response = await api.get('/super-admin/clubs');
      return response.data;
    },
    staleTime: 60_000,
  });

  type ClubEntry = { id: string; name: string; logoUrl: string | null; isDemo: boolean; tenant: { id: string } };
  const clubs: Array<{ id: string; tenantId: string; name: string; logoUrl: string | null }> = (
    clubListData?.clubs ?? []
  )
    .filter((entry: ClubEntry) => !entry.isDemo)
    .map((entry: ClubEntry) => ({
      id:       entry.id,
      tenantId: entry.tenant.id,
      name:     entry.name,
      logoUrl:  entry.logoUrl,
    }));

  // Resolve the full club object for the currently active ID so we can
  // display the club name rather than just the ID.
  const activeClub = clubs.find((club) => club.id === activeClubId) ?? null;

  /**
   * Called when the admin selects a different club from the dropdown.
   * Updates both the club ID (x-club-id header) and the tenant ID (X-Tenant-Id header)
   * so all subsequent API calls are scoped to the newly selected club, and clears the
   * query cache so every page shows its loading state instead of the previous club's data.
   */
  const handleClubChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedClubId = event.target.value || null;
    const selectedClub = clubs.find((club) => club.id === selectedClubId) ?? null;

    setActiveClubId(selectedClubId);

    if (selectedClub) {
      activateClubContext(selectedClub.id, selectedClub.tenantId, queryClient);
    } else {
      // Placeholder picked — selection cleared; tenant context intentionally kept.
      setSuperAdminClubId(selectedClubId);
      queryClient.invalidateQueries();
    }
  };

  return (
    <div className="mx-3 mt-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
      {/* Section label */}
      <p className="text-[9px] font-semibold text-amber-500/60 uppercase tracking-[0.14em] mb-1.5">
        Controlling Club
      </p>

      {/* Currently active club name — prominent so the admin always knows which club they are in */}
      {activeClub ? (
        <p className="text-xs font-semibold text-amber-300 mb-2 truncate" title={activeClub.name}>
          {activeClub.name}
        </p>
      ) : (
        <p className="text-xs text-amber-600/80 mb-2 italic">{t('layout.noClubSelected')}</p>
      )}

      {/* Dropdown to switch between clubs */}
      <select
        value={activeClubId ?? ''}
        onChange={handleClubChange}
        className="w-full px-2.5 py-1.5 bg-black/20 border border-amber-500/20 rounded-lg text-xs text-slate-400 focus:outline-none focus:border-amber-500/40 transition-all cursor-pointer"
        aria-label={t('layout.selectClub')}
      >
        <option value="" className="bg-[var(--bg-card)]">{t('layout.switchClub')}</option>
        {clubs.map((club) => (
          <option key={club.id} value={club.id} className="bg-[var(--bg-card)]">
            {club.name}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Sidebar content (shared between desktop sidebar + mobile drawer) ──────────
//
// `collapsed` is the 64px icon rail. It costs labels, not capabilities: every
// control keeps its place and stays reachable, it just loses its text and gains a
// tooltip. Only the desktop aside passes it — the mobile drawer is always full
// width, which is also why the collapse toggle is gated on `onToggleCollapse`
// being supplied rather than on `collapsed` itself. The drawer has its own close
// button and a second chevron in there would be a control that does nothing.
function SidebarContent({
  onNavClick,
  collapsed = false,
  onToggleCollapse,
}: {
  onNavClick?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const router = useRouter();
  const { theme, toggleTheme } = useTheme();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    staleTime: PROFILE_STALE_TIME,
  });

  useEffect(() => { ensureShellStyle(); }, []);

  const isSuperAdmin = profile?.role === 'super_admin';
  // Page visibility comes from Zero.Config's matrix (what Admin → Permissions
  // actually writes) — NOT profile.allowedTabs, which was never wired up. Carries
  // the super-admin global toggle too; see lib/tabPermissions.ts.
  // The caller's OWN row. This used to read the whole club's matrix off an /admin/* route,
  // which handed every logged-in user every other role's configuration.
  const { data: tabPermissions } = useMyPermissions();
  const canSee = (key: string) => canSeeTab(key, profile?.role, tabPermissions);
  const isAdmin = canSee('admin');

  // No join-requests query here. One used to poll GET /auth/join-requests every 30s for every
  // admin, on every page, for the whole session — and bound its pendingCount to nothing. Every
  // `badge` in COCKPIT_NAV is a hardcoded 0, so no badge has ever painted; the count was computed
  // and dropped. The two surfaces that actually review join requests (Admin → Users and the
  // Notifications page) fetch it themselves while open, so nothing is lost. If a nav badge is ever
  // wanted, add the query back WITH the binding in the same change, not before it.
  const displayName: string = profile?.displayName ?? profile?.email ?? 'User';
  const roleLabel = ROLE_LABELS[profile?.role ?? ''] ?? profile?.role ?? '';
  const clubLogo: string | null = profile?.club?.logoUrl ?? null;
  const clubName: string = profile?.club?.name ?? '';

  // Inject club brand colors as CSS variables. Shared with the player profile,
  // which renders outside this Layout and needs the same variables.
  useClubBranding(profile?.club?.primaryColor, profile?.club?.secondaryColor);

  // Which way the chevron points. The aside is a flex-row child, so RTL already
  // moves it to the right edge on its own — the arrow is the one thing that has
  // to be told, because "collapse" travels toward the OUTER edge and that edge
  // swaps sides with the direction.
  const isRtl = i18n.dir() === 'rtl';
  const ToggleIcon = (collapsed ? !isRtl : isRtl) ? ChevronsRight : ChevronsLeft;
  const toggleLabel = collapsed ? t('layout.expandSidebar') : t('layout.collapseSidebar');

  const collapseToggle = onToggleCollapse ? (
    <button
      onClick={onToggleCollapse}
      className="p-1.5 rounded-lg shell-icon-btn shell-focus"
      title={toggleLabel}
      aria-label={toggleLabel}
      aria-expanded={!collapsed}
    >
      <ToggleIcon className="w-4 h-4" />
    </button>
  ) : null;

  // Defined once and placed by both layouts. The theme toggle in particular has
  // to survive the collapse: it is the only theme control staff have anywhere in
  // the app, so hiding it behind an expand would be a straight regression.
  const themeButton = (
    <button
      onClick={toggleTheme}
      className="p-1.5 rounded-lg shell-icon-btn shell-focus"
      title={theme === 'dark' ? t('layout.switchToLightMode') : t('layout.switchToDarkMode')}
      aria-label={theme === 'dark' ? t('layout.switchToLightMode') : t('layout.switchToDarkMode')}
    >
      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );

  const signOutButton = (
    <button
      onClick={async () => {
        await endSession(queryClient);
        router.navigate({ to: '/login' });
      }}
      title={t('layout.signOut')}
      aria-label={t('layout.signOut')}
      className="p-1.5 rounded-lg shrink-0 shell-icon-btn shell-icon-btn-danger shell-focus"
    >
      <LogOut className="w-4 h-4" />
    </button>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Top accent hairline. Club primary only — pairing it with --club-secondary
          made half the line vanish for any club whose second colour is black or
          white, which is most of them. */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(to right, transparent, var(--club-primary), transparent)` }} />

      {/* Argus Sports brand mark + collapse toggle */}
      {collapsed ? (
        <div style={{ padding: '20px 0 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <ArgusLockup tile={36} title={16} sub={11} collapsed />
          {collapseToggle}
        </div>
      ) : (
        <div style={{ padding: '24px 14px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ArgusLockup tile={44} title={16} sub={11} />
          </div>
          {collapseToggle}
        </div>
      )}

      {/* Club info pill — collapses to a squircle carrying the club's logo, or its
          initials when there is no logo. Same two tokens as the pill (--club-primary
          at 9% for the tint, --club-ink for the text), so the contrast guarantee is
          the pill's guarantee and no new colour enters the shell. */}
      {clubName && (collapsed ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <div
            title={`${t('layout.club')}: ${clubName}`}
            style={{
              width: 36, height: 36, borderRadius: 10, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'color-mix(in srgb, var(--club-primary) 9%, transparent)',
              border: '1px solid color-mix(in srgb, var(--club-primary) 26%, transparent)',
            }}
          >
            {clubLogo ? (
              <img src={clubLogo} alt={clubName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--club-ink)', letterSpacing: '0.02em' }}>
                {getInitials(clubName)}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          margin: '0 10px 12px', padding: '8px 11px', borderRadius: 9,
          display: 'flex', alignItems: 'center', gap: 9,
          background: 'color-mix(in srgb, var(--club-primary) 9%, transparent)',
          border: '1px solid color-mix(in srgb, var(--club-primary) 26%, transparent)',
        }}>
          {/* Same squircle as the collapsed rail's club pill — logo when the club has
              one configured, initials otherwise. Was missing from this expanded state
              entirely (text-only pill, no image ever rendered), even though the logo
              was already fetched and ready — this is the ONLY place a non-collapsed
              sidebar shows it. */}
          <div style={{
            width: 30, height: 30, borderRadius: 8, overflow: 'hidden', flex: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'color-mix(in srgb, var(--club-primary) 14%, transparent)',
          }}>
            {clubLogo ? (
              <img src={clubLogo} alt={clubName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--club-ink)', letterSpacing: '0.02em' }}>
                {getInitials(clubName)}
              </span>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            {/* --club-ink, NOT --club-primary: the raw brand as a label on this
                tint is what made "CLUB" ~1.2:1 for a yellow club in light mode. */}
            <div style={{ fontSize: 11, color: 'var(--club-ink)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 2 }}>{t('layout.club')}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clubName}</div>
          </div>
        </div>
      ))}

      {/* Nav */}
      <nav className={cn('flex-1 mt-1 space-y-0.5 overflow-y-auto', collapsed ? 'px-2' : 'px-3')}>
        {COCKPIT_NAV.filter((item) => {
          // Every page is gated by the club's permission matrix. This used to consult
          // canSee() for ONLY 'loadrisk' and admin-role items and fall through to
          // `return true` for everything else, so unticking a page in Admin →
          // Permissions had no effect on the sidebar at all.
          if (item.type === 'item') {
            // Admin also hosts the per-user Preferences (language) section, which every
            // role needs, so the entry stays visible; the admin-only tabs inside it are
            // gated separately. Its own page guard still applies.
            if (item.key === 'admin') return true;
            if (item.key === 'superAdmin') return isSuperAdmin;
            // NOTE: fitness_trainer used to be hardcoded to always see Load & Risk,
            // which silently overrode an admin who had deliberately unticked it. The
            // default matrix already grants loadrisk to fitness_trainer, so the override
            // only ever differed when it was contradicting an explicit decision.
            return canSee(item.key);
          }
          // Dividers: a role-gated separator is hidden from roles that can't see the
          // rows beneath it, so the sidebar never ends on a stray rule. The only
          // divider currently carries no role, because Admin/Preferences now sits
          // below it for every role — there is always a row after it.
          return item.role === 'admin' ? isAdmin : true;
        }).map((item, idx) => {
          const C = LIVE_COLORS;
          if (item.type === 'divider') {
            return (
              <div key={`divider-${idx}`} style={{ height: 1, background: C.border, margin: '6px 4px' }} />
            );
          }
          // type === 'item'
          const isActive = location.pathname === item.path;
          const C2 = LIVE_COLORS;
          const { Icon } = item;
          const label = item.key === 'admin' && !isAdmin ? t('nav.preferences') : t(`nav.${item.key}`);
          const hasBadge = item.badge !== undefined && item.badge > 0;
          return (
            <Link
              key={item.key}
              to={item.path}
              onClick={onNavClick}
              className="cockpit-nav-hover shell-focus"
              aria-current={isActive ? 'page' : undefined}
              // Collapsed, the label leaves the page but not the accessible name:
              // title for the pointer, aria-label so the link is still announced
              // as "Load & Risk" rather than as its href.
              title={collapsed ? label : undefined}
              aria-label={collapsed ? label : undefined}
              style={{
                display: 'flex', alignItems: 'center',
                gap: collapsed ? 0 : 10,
                justifyContent: collapsed ? 'center' : undefined,
                padding: collapsed ? '9px 0' : '9px 10px',
                fontSize: 13,
                lineHeight: 1.2,
                borderRadius: 7,
                textDecoration: 'none',
                // The selected state is a solid bar in the RAW brand (a shape —
                // contrast does not apply) plus a light tint, with the LABEL in
                // --club-ink. The old treatment painted the label in the raw
                // brand over a 20% wash of the same colour, which is what made
                // it read as muddy olive. borderInlineStart, not borderLeft, so
                // the bar moves to the right edge under RTL.
                borderInlineStart: `3px solid ${isActive ? 'var(--club-primary)' : 'transparent'}`,
                ...(isActive
                  ? {
                      background: 'color-mix(in srgb, var(--club-primary) 8%, transparent)',
                      color: 'var(--club-ink)',
                      fontWeight: 700,
                    }
                  // Navigation is not a hint: this was --c-text-dim (2.5:1).
                  : { color: C2.gray, fontWeight: 500 }),
              }}
            >
              <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                <Icon size={16} strokeWidth={isActive ? 2.25 : 2} aria-hidden="true" />
                {/* Collapsed, the count rides the icon's corner. A 64px row has no
                    space for the pill, and degrading to a bare dot would throw the
                    number away — "3 pending" is not the same as "some pending". */}
                {collapsed && hasBadge && (
                  <span style={{
                    position: 'absolute', top: -5, insetInlineEnd: -7,
                    minWidth: 14, height: 14, padding: '0 3px',
                    background: item.badgeColor,
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 700,
                    lineHeight: '14px',
                    borderRadius: 7,
                    textAlign: 'center',
                  }}>
                    {item.badge}
                  </span>
                )}
              </span>
              {!collapsed && <span style={{ flex: 1 }}>{label}</span>}
              {!collapsed && hasBadge && (
                <span style={{
                  marginLeft: 'auto',
                  background: item.badgeColor,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 8,
                }}>
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom. Collapsed, the user card drops its chrome and becomes the avatar
          alone — the name and role move into its tooltip rather than disappearing. */}
      {collapsed ? (
        <div className="px-2 pb-4 flex flex-col items-center gap-1.5">
          {themeButton}
          <div title={roleLabel ? `${displayName} · ${roleLabel}` : displayName} className="py-0.5">
            <PlayerAvatar name={displayName} imageUrl={proxiedImageUrl(profile?.player?.imageUrl) ?? null} size="sm" />
          </div>
          {signOutButton}
        </div>
      ) : (
        <div className="px-3 pb-4 space-y-2">
          {/* Theme toggle — language now lives in Admin → Preferences (every role) */}
          <div className="flex items-center justify-end px-2 py-1.5">
            {themeButton}
          </div>

          {/* User card */}
          <div
            className="relative rounded-xl p-3"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-strong)' }}
          >
            <div className="flex items-center gap-2.5">
              <PlayerAvatar name={displayName} imageUrl={proxiedImageUrl(profile?.player?.imageUrl) ?? null} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="truncate leading-none" style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{displayName}</p>
                {/* Was text-slate-600 at 10px — 2.5:1, the least readable text in
                    the whole shell. */}
                <p className="leading-none" style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{roleLabel}</p>
              </div>
              {signOutButton}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mobile top bar with club logo + player avatar ────────────────────────────
function MobileTopbar({ onMenuOpen }: { onMenuOpen: () => void }) {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();

  const { data: profile } = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    staleTime: PROFILE_STALE_TIME,
  });

  const playerName = profile?.player
    ? i18n.language === 'he' && (profile.player.hebrewFirstName || profile.player.hebrewLastName)
      ? `${profile.player.hebrewFirstName ?? ''} ${profile.player.hebrewLastName ?? ''}`.trim()
      : `${profile.player.firstName} ${profile.player.lastName}`
    : profile?.displayName ?? '';

  const clubLogo: string | null = profile?.club?.logoUrl ?? null;
  const clubName: string = profile?.club?.name ?? '';

  return (
    <header className="md:hidden flex items-center gap-3 h-[var(--mobile-topbar-h)] px-4 bg-[var(--bg-sidebar)] border-b border-[var(--border-default)] sticky top-0 z-30">
      <button
        onClick={onMenuOpen}
        className="p-2 rounded-lg shell-icon-btn shell-focus"
        aria-label={t('common.openMenu')}
      >
        <Menu className="w-5 h-5" />
      </button>
      {/* Same club squircle as the sidebar's pill (logo, or initials when the
          club has none configured) — this bar is the one thing that's actually
          on screen at all times on mobile, where the sidebar is a drawer that
          stays closed, so it's the only reliably "always visible" spot for it. */}
      {clubName && (
        <div
          title={`${t('layout.club')}: ${clubName}`}
          style={{
            // Same 40px tile as ArgusLockup's own mark just below, so the two marks read
            // as a pair rather than one looking like an afterthought next to the other.
            width: 40, height: 40, borderRadius: 9, overflow: 'hidden', flex: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'color-mix(in srgb, var(--club-primary) 9%, transparent)',
            border: '1px solid color-mix(in srgb, var(--club-primary) 26%, transparent)',
          }}
        >
          {clubLogo ? (
            <img src={clubLogo} alt={clubName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--club-ink)', letterSpacing: '0.02em' }}>
              {getInitials(clubName)}
            </span>
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <ArgusLockup tile={40} title={15} sub={10} />
      </div>

      {/* Theme toggle — language now lives in Admin → Preferences (every role) */}
      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg shell-icon-btn shell-focus"
        aria-label={theme === 'dark' ? t('layout.switchToLightMode') : t('layout.switchToDarkMode')}
      >
        {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>

      <PlayerAvatar name={playerName} imageUrl={proxiedImageUrl(profile?.player?.imageUrl) ?? null} size="sm" />
    </header>
  );
}

// ── Cockpit context bar — real fixture data ──────────────────────────────────
function CockpitContextBar() {
  const C = LIVE_COLORS;
  const { data: fixture, isLoading } = useNextFixture();

  if (isLoading || !fixture) return null;

  const md = daysAwayToMd(fixture.daysAway);
  const timing = daysAwayToLabel(fixture.daysAway);
  const venue = fixture.isHome ? 'Home' : 'Away';

  return (
    <div style={{
      padding: '6px 16px',
      background: C.surface,
      borderBottom: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <MDChip md={md} />
      {fixture.opponent && (
        <>
          <span style={{ fontSize: 11, color: C.gray }}>
            vs <span style={{ fontWeight: 600, color: C.white }}>{fixture.opponent}</span> · {timing} · {venue}
          </span>
        </>
      )}
      <div style={{ flex: 1 }} />
      {fixture.competition && (
        <span style={{ fontSize: 10, color: C.muted }}>{fixture.competition}</span>
      )}
    </div>
  );
}

// ── Main Layout ───────────────────────────────────────────────────────────────
export function Layout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop rail only — the mobile drawer is always full width.
  const [sidebarCollapsed, toggleSidebar] = useSidebarCollapsed();

  // Cache-shared with the other profile queries in this file (ArgusLockup, SidebarContent,
  // MobileTopbar all query the same queryKey) — so this costs no extra request.
  const { data: profile } = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    staleTime: PROFILE_STALE_TIME,
  });

  // Language lives HERE, in Layout, rather than in SidebarContent where useClubBranding sits.
  // SidebarContent is mounted TWICE at once — the mobile drawer and the desktop aside are both
  // unconditionally rendered, with visibility handled by CSS, not JS gating — so calling this
  // there fired i18n.changeLanguage twice per change, and i18next emits `languageChanged` (a
  // full-app re-render) on every completed call with no already-active guard. Layout mounts once.
  // useClubBranding stays doubled in SidebarContent on purpose: it's a synchronous, idempotent
  // CSS-variable write with no event emission, so calling it twice is indistinguishable from once.
  useClubLanguage(profile?.club?.language);

  // The shell's sync banner FOLLOWS a sync; it does not go looking for one. While a sync is
  // running this polls hard, and while nothing is running it does not poll AT ALL — an idle
  // 30s tick here was a request every 30 seconds on every page of the app, for every user, for
  // the whole session, to be told "idle" each time. Layout wraps every route under `appRoute`
  // and never unmounts on navigation, so what is left is one request per app load.
  //
  // Restarting is covered without a heartbeat. A sync started from the Integration Hub
  // invalidates this exact query key, which refetches here too and turns the 2s interval back
  // on; the Hub and the Logs page each poll on their own while open, and share this cache. What
  // that leaves is a SCHEDULED sync beginning while a coach sits on some other page: the banner
  // appears on the next focus rather than within 30s. Deliberate — the banner is a courtesy, and
  // the only way to get both zero idle traffic and instant discovery is a push channel the
  // progress endpoint does not have.
  const { data: progress } = useQuery({
    queryKey: ['sync-progress'],
    queryFn: () => api.get('/catapult/sync/progress').then(r => r.data),
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 2000 : false),
    // Overrides the app-wide default (queryClient.ts turns this off everywhere). Coming back to
    // the tab is the one cheap moment to notice a sync that started while nobody was watching.
    refetchOnWindowFocus: true,
  });

  const isSyncing = progress?.status === 'running';

  const prevStatusRef = useRef<string | null>(null);
  const [justCompleted, setJustCompleted] = useState(false);
  const [completedProgress, setCompletedProgress] = useState<typeof progress | null>(null);

  useEffect(() => {
    if (prevStatusRef.current === 'running' && progress?.status === 'idle') {
      setJustCompleted(true);
      setCompletedProgress(progress);
      const timer = setTimeout(() => setJustCompleted(false), 8000);
      return () => clearTimeout(timer);
    }
    if (progress?.status) prevStatusRef.current = progress.status;
  }, [progress?.status]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[var(--bg-page)]">

      {/* ── Mobile topbar ───────────────────────────────────────────────── */}
      <MobileTopbar onMenuOpen={() => setMobileOpen(true)} />

      {/* ── Mobile backdrop ─────────────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Mobile drawer ───────────────────────────────────────────────── */}
      <div className={cn(
        'md:hidden fixed inset-y-0 left-0 z-50 w-72 bg-[var(--bg-sidebar)] border-r border-[var(--border-default)] flex flex-col',
        'transition-transform duration-300 ease-in-out',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
      )}>
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4 p-1.5 rounded-lg z-10 shell-icon-btn shell-focus"
          aria-label={t('layout.closeMenu')}
        >
          <X className="w-4 h-4" />
        </button>
        <SidebarContent onNavClick={() => setMobileOpen(false)} />
      </div>

      {/* ── Desktop sidebar ─────────────────────────────────────────────── */}
      {/* overflow-x-hidden as well as -y: at 64px the nav's own content is what
          would otherwise produce a horizontal scrollbar inside the rail. */}
      <aside
        className={cn(
          'hidden md:flex bg-[var(--bg-sidebar)] border-r border-[var(--border-default)]',
          'flex-col shrink-0 sticky top-0 h-screen overflow-y-auto overflow-x-hidden',
          'transition-[width] duration-200 ease-in-out',
          sidebarCollapsed ? 'w-16' : 'w-64',
        )}
      >
        <SidebarContent collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />
      </aside>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Cockpit context bar — real fixture data */}
        {COCKPIT_ROUTES.includes(location.pathname) && <CockpitContextBar />}
        {isSyncing && (
          <>
            <div className="h-0.5 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500 animate-pulse" />
            <div className="px-4 md:px-6 py-2 bg-blue-500/5 border-b border-blue-500/10">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 text-xs text-blue-400 min-w-0">
                  <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                  <span className="truncate">
                    {t('layout.syncing', {
                      processed: progress?.progress?.processed ?? 0,
                      total: progress?.progress?.total ?? '…',
                    })}
                    {progress?.currentActivity ? ` · ${progress.currentActivity}` : ''}
                  </span>
                </div>
                <span className="text-xs text-blue-500 font-mono ml-2 shrink-0">{progress?.progress?.percent ?? 0}%</span>
              </div>
              <div className="h-1 bg-blue-500/15 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full transition-all duration-1000" style={{ width: `${progress?.progress?.percent ?? 0}%` }} />
              </div>
              {progress?.db && (
                <div className="flex gap-2 mt-1.5 text-[10px] text-blue-600 flex-wrap">
                  <span>
                    {t('layout.syncStats', {
                      players: progress.db.players,
                      sessions: progress.db.sessions,
                      snapshots: progress.db.snapshots.toLocaleString(),
                    })}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
        {justCompleted && (
          <div className="px-4 md:px-6 py-2 bg-emerald-500/5 border-b border-emerald-500/10 flex items-center gap-2 text-xs text-emerald-400">
            <CheckCircle className="w-3 h-3 shrink-0" />
            <span className="truncate">
              {t('layout.syncComplete', {
                players: completedProgress?.db.players,
                sessions: completedProgress?.db.sessions,
                snapshots: completedProgress?.db.snapshots.toLocaleString(),
              })}
            </span>
          </div>
        )}
        {progress?.status === 'error' && progress?.errorMessage && (
          <div className="px-4 md:px-6 py-2.5 bg-red-500/8 border-b border-red-500/20 flex items-start gap-2.5 text-xs text-red-300">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-red-400" />
            <div className="min-w-0">
              <span className="font-semibold text-red-400">{t('layout.syncError')} </span>
              <span className="font-mono break-all">{progress.errorMessage}</span>
              <span className="text-red-500 ml-2">— <a href="/logs" className="underline underline-offset-2 hover:text-red-300 transition-colors">{t('layout.viewLogs')}</a></span>
            </div>
          </div>
        )}
        {children}
      </main>
      <ToastHost />
      <ConfirmHost />
    </div>
  );
}
